import { apiService, API_ENDPOINTS } from "@/shared/services/api"
import type { APIResponse } from "@/shared/types"

export type HRManagerType = 'PAYROLL' | 'OPERATIONS' | 'ONBOARDING' | 'ONBOARDING_ASSISTANT' | 'QUALITY_CONTROL'

export interface HRManager {
  id: string
  email: string
  firstName: string
  lastName: string
  hrManagerType: HRManagerType | null
  organizationId: string | null
  isActive: boolean
  createdAt: string
  lastLoginAt: string | null
  accountAge: number
  organization?: {
    id: string
    name: string
    type: string
  } | null
}

export interface HRManagerFilters {
  search?: string
  hrManagerType?: string
  organizationId?: string
  isActive?: boolean
  page?: number
  limit?: number
}

export interface CreateHRManagerData {
  email: string
  firstName: string
  lastName: string
  hrManagerType: HRManagerType
  organizationId?: string
}

export interface UpdateHRManagerData {
  firstName?: string
  lastName?: string
  hrManagerType?: HRManagerType
  organizationId?: string | null
  isActive?: boolean
}

export interface HRManagerStats {
  total: number
  active: number
  inactive: number
  unassignedType: number
  byType: Array<{
    type: HRManagerType | null
    count: number
  }>
}

export class HRManagerService {
  /**
   * Get paginated list of HR Managers with filters
   */
  static async getHRManagers(filters: HRManagerFilters = {}): Promise<{
    hrManagers: HRManager[]
    pagination: {
      total: number
      page: number
      limit: number
      totalPages: number
    }
  }> {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })

    const response = await apiService.get<APIResponse<{
      hrManagers: HRManager[]
      pagination: {
        total: number
        page: number
        limit: number
        totalPages: number
      }
    }>>(`/hr-managers?${params.toString()}`)
    
    // Backend returns { success, message, data: { hrManagers: [], pagination: {} } }
    return response.data.data!
  }

  /**
   * Get HR Manager by ID with full details
   */
  static async getHRManagerById(id: string): Promise<HRManager> {
    const response = await apiService.get<APIResponse<HRManager>>(`/hr-managers/${id}`)
    return response.data.data!
  }

  /**
   * Create new HR Manager
   */
  static async createHRManager(data: CreateHRManagerData): Promise<{
    hrManager: HRManager
    message: string
  }> {
    const response = await apiService.post<APIResponse<{
      hrManager: HRManager
      message: string
    }>>('/hr-managers', data)
    return response.data.data!
  }

  /**
   * Update HR Manager
   */
  static async updateHRManager(id: string, data: UpdateHRManagerData): Promise<{
    hrManager: HRManager
    message: string
  }> {
    const response = await apiService.put<APIResponse<{
      hrManager: HRManager
      message: string
    }>>(`/hr-managers/${id}`, data)
    return response.data.data!
  }

  /**
   * Delete HR Manager (soft delete)
   */
  static async deleteHRManager(id: string): Promise<{ message: string }> {
    const response = await apiService.delete<APIResponse<{ message: string }>>(`/hr-managers/${id}`)
    return response.data.data!
  }

  /**
   * Toggle HR Manager status (activate/deactivate)
   */
  static async toggleHRManagerStatus(id: string, isActive: boolean): Promise<{
    hrManager: HRManager
    message: string
  }> {
    const response = await apiService.patch<APIResponse<{
      hrManager: HRManager
      message: string
    }>>(`/hr-managers/${id}/status`, { isActive })
    return response.data.data!
  }

  /**
   * Reset HR Manager password
   */
  static async resetHRManagerPassword(id: string): Promise<{ message: string }> {
    const response = await apiService.post<APIResponse<{ message: string }>>(`/hr-managers/${id}/reset-password`)
    return response.data.data!
  }

  /**
   * Get HR Manager statistics
   */
  static async getHRManagerStats(): Promise<HRManagerStats> {
    const response = await apiService.get<APIResponse<HRManagerStats>>('/hr-managers/stats')
    return response.data.data!
  }
}
